import React from 'react';
import { Link } from 'react-router-dom';
import { capitalizeFirstLetter, getDate, trimDecimals } from '@einhaus/meteoric-helpers';
import type { PortfolioBalanceSummary, PortfolioWithAccountingFields } from './types';
import { formatCurrency } from './helpers';

interface PortfolioSummaryCardProps {
    portfolio: PortfolioWithAccountingFields;
    portfolioId: number;
    pendingTrades: number;
    balanceSummary: PortfolioBalanceSummary;
}

const gainColor = (value: number) => (value > 0 ? 'text-profit-green' : value < 0 ? 'text-red-500' : 'text-white');
const signedCurrency = (value: number) => `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatCurrency(Math.abs(value))}`;
const readableTime = (dateTime: string) => getDate({ date: dateTime, format: 'readable1', timezone: 'America/New_York' });

const PortfolioSummaryCard: React.FC<PortfolioSummaryCardProps> = ({ portfolio, portfolioId, pendingTrades, balanceSummary }) => {
    const hasDailyBaseline =
        balanceSummary.dailyBaselineAsOf !== null &&
        balanceSummary.dailyBaselineAsOf.slice(0, 10) === balanceSummary.valuationAsOf.slice(0, 10);
    const hasShortPositions = balanceSummary.investedAmount === null;
    const cards = [
        {
            label: hasShortPositions ? 'Net open positions value' : 'Open positions value',
            value: signedCurrency(balanceSummary.markedOpenPositionValue).replace(/^\+/, ''),
            detail: hasShortPositions ? 'Holdings less the cost to close short positions' : 'Current estimated value of your holdings',
            color: 'text-white'
        },
        {
            label: 'Today’s gain/loss',
            value: hasDailyBaseline ? signedCurrency(balanceSummary.todayChange) : 'Unavailable',
            detail: hasDailyBaseline
                ? 'Open and closed trade results; excludes funding changes'
                : 'Waiting for a recorded start-of-day balance',
            color: hasDailyBaseline ? gainColor(balanceSummary.todayChange) : 'text-zinc-400'
        },
        {
            label: 'Total gain/loss',
            value: signedCurrency(balanceSummary.totalPnl),
            detail: 'Gains/losses since inception',
            color: gainColor(balanceSummary.totalPnl)
        },
        {
            label: 'Trading budget',
            value: formatCurrency(balanceSummary.reportingCapitalBase),
            detail: 'Amount assigned to this portfolio',
            color: 'text-white'
        }
    ];

    return (
        <div className="mb-4 rounded-lg bg-[#1a1a1a] p-4 text-white shadow-md">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h5 className="text-xl font-medium">Portfolio Summary</h5>
                    <div className="text-sm text-zinc-400">
                        {capitalizeFirstLetter(portfolio.type)} • Pending trades:{' '}
                        <Link className="text-blue-400 hover:text-blue-300" to={`/user/portfolioPendingTrades/${portfolioId}`}>
                            {pendingTrades}
                        </Link>
                    </div>
                </div>
                {balanceSummary.valuationAsOf && (
                    <div className="text-xs text-zinc-400">Summary updated {readableTime(balanceSummary.valuationAsOf)} ET</div>
                )}
            </div>

            {balanceSummary.isBrokerageDataStale ? (
                <div className="mb-4 rounded-md border border-red-700/60 bg-red-500/10 p-3 text-sm text-red-100" role="alert">
                    <div className="font-medium">Brokerage balance needs an update</div>
                    <div className="mt-1">
                        New entries are blocked until a fresh brokerage balance is available. Exits and risk-reducing orders remain
                        available. Portfolio value below still reflects the recorded budget and position values.
                    </div>
                </div>
            ) : balanceSummary.isAllocationLimited ? (
                <div className="mb-4 rounded-md border border-yellow-700/60 bg-yellow-500/10 p-3 text-sm text-yellow-100" role="alert">
                    <div className="font-medium">Your broker cannot support the full portfolio allocation</div>
                    <div className="mt-1">
                        New entries use a portfolio equity limit of {formatCurrency(balanceSummary.currentEquity)}, which is{' '}
                        {formatCurrency(balanceSummary.allocationShortfall)} below the calculated portfolio value. View brokerage limits for
                        details.
                    </div>
                </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                {cards.map((card, index) => (
                    <div className="rounded-lg border border-zinc-800 bg-[#121212] p-3 sm:p-4" key={card.label}>
                        <div className="text-sm text-zinc-400">{card.label}</div>
                        <div className={`mt-2 break-words text-xl font-semibold sm:text-2xl ${card.color}`}>{card.value}</div>
                        {index === 1 && hasDailyBaseline && balanceSummary.lastClosingEquity > 0 && (
                            <div className={`mt-1 text-xs ${gainColor(balanceSummary.todayChangePercent)}`}>
                                {balanceSummary.todayChangePercent > 0 ? '+' : ''}
                                {trimDecimals(balanceSummary.todayChangePercent)}% of starting portfolio value
                            </div>
                        )}
                        {index === 2 && (
                            <div className="mt-1 text-xs text-zinc-400">
                                <div>Closed trades &amp; costs: {signedCurrency(balanceSummary.realizedPnl)}</div>
                                <div className="mt-1">Open positions: {signedCurrency(balanceSummary.unrealizedPnl)}</div>
                            </div>
                        )}
                        <div className="mt-1 text-xs text-zinc-400">{card.detail}</div>
                    </div>
                ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3 rounded-md border border-zinc-800 bg-[#151515] p-3 text-sm">
                {balanceSummary.investedAmount !== null && (
                    <div>
                        <div className="text-zinc-400">Amount invested</div>
                        <div className="mt-1 font-medium">{formatCurrency(balanceSummary.investedAmount)}</div>
                        <div className="mt-1 text-xs text-zinc-500">Entry cost of holdings you still own, before fees</div>
                    </div>
                )}
                {balanceSummary.uninvestedBudget !== null && (
                    <div>
                        <div className="text-zinc-400">
                            {balanceSummary.uninvestedBudget < 0 ? 'Budget overdrawn' : 'Uninvested budget'}
                        </div>
                        <div className="mt-1 font-medium">{formatCurrency(Math.abs(balanceSummary.uninvestedBudget))}</div>
                        <div className="mt-1 text-xs text-zinc-500">Includes gains/losses from closed trades</div>
                    </div>
                )}
                <div>
                    <div className="text-zinc-400">Portfolio value</div>
                    <div className="mt-1 font-medium">{signedCurrency(balanceSummary.performanceEquity).replace(/^\+/, '')}</div>
                    <div className="mt-1 text-xs text-zinc-500">Trading budget plus total gain/loss</div>
                </div>
            </div>
            <div className="mt-2 text-xs text-zinc-400">
                {hasShortPositions
                    ? portfolio.tradingAccountId
                        ? 'Short positions use collateral. Their trading limits are shown in brokerage details.'
                        : 'Short positions use collateral. Net position value includes the amount owed to close them.'
                    : 'Uninvested budget is separate from buying power. Pending orders, your broker and trading rules may limit new trades.'}
            </div>
            {hasDailyBaseline && balanceSummary.dailyBaselineAsOf && (
                <div className="mt-1 text-xs text-zinc-500">Today’s baseline: {readableTime(balanceSummary.dailyBaselineAsOf)} ET</div>
            )}
        </div>
    );
};

export default PortfolioSummaryCard;
